import { watch, type Ref, type ComputedRef } from 'vue';
import { AssetType, LiabilityType } from '@/helpers/const';
import type { LoanApplication, MotorVehicleAsset, VehicleLiability } from '@/types';

/**
 * Auto-populates make and model fields for motor vehicle assets/liabilities
 * when there's already an existing motor vehicle scenario in the loan application.
 *
 * @param loanApp - The loan application computed ref
 * @param type - The current type value (ref or computed)
 * @param make - The current make value (ref or computed)
 * @param model - The current model value (ref or computed)
 * @param setFieldValue - Function to set field values (from vee-validate useForm)
 * @param isAsset - Whether this is for an asset (true) or liability (false)
 * @param applicantId - The identifier for the current applicant/card
 */
export function useAutoPopulateMotorVehicle(
  loanApp: ComputedRef<LoanApplication>,
  applicantId: string,
  type: Ref<string | undefined> | ComputedRef<string | undefined>,
  make: Ref<string | undefined> | ComputedRef<string | undefined>,
  model: Ref<string | undefined> | ComputedRef<string | undefined>,
  setFieldValue: (field: 'make' | 'model', value: string) => void,
  isAsset: boolean,
) {
  const targetType = isAsset ? AssetType.MOTOR_VEHICLES : LiabilityType.VEHICLE;

  // Track if auto-population has already been attempted for this field set
  let hasAutoPopulated = false;

  watch(
    () => type.value,
    (newType, oldType) => {
      // Prevent auto-population if:
      // 1. Type hasn't changed to target type
      // 2. We've already auto-populated once
      // 3. Either make or model already has values
      if (newType !== targetType || hasAutoPopulated || make.value || model.value) {
        return;
      }

      // Also prevent if type changed from target type to something else and back
      // This ensures we don't re-populate if user manually cleared and changed type
      if (oldType === targetType && !make.value && !model.value) {
        return;
      }

      // Find the applicant by ID and get their index
      const applicantIndex = loanApp.value.applicants.findIndex((a) => a.id === applicantId);
      if (applicantIndex === -1) {
        return; // Applicant not found, skip auto-population
      }

      const financials = loanApp.value.financials[`applicant-${applicantIndex}`];

      // Check for existing motor vehicle assets (scoped to this applicant only)
      const existingMotorVehicleAsset = financials?.assets
        ?.filter(
          (asset): asset is MotorVehicleAsset =>
            asset.type === AssetType.MOTOR_VEHICLES &&
            'make' in asset &&
            'model' in asset &&
            !!asset.make &&
            !!asset.model,
        )
        .find((asset) => asset.make && asset.model);

      // Check for existing motor vehicle liabilities (scoped to this applicant only)
      const existingMotorVehicleLiability = financials?.liabilities
        ?.filter(
          (liability): liability is VehicleLiability =>
            liability.type === 'vehicle' &&
            'make' in liability &&
            'model' in liability &&
            !!liability.make &&
            !!liability.model,
        )
        .find((liability) => liability.make && liability.model);

      // Use the first available motor vehicle (prefer asset over liability)
      const existingMotorVehicle = existingMotorVehicleAsset || existingMotorVehicleLiability;

      if (
        existingMotorVehicle &&
        'make' in existingMotorVehicle &&
        'model' in existingMotorVehicle &&
        existingMotorVehicle.make &&
        existingMotorVehicle.model
      ) {
        // Batch updates to prevent multiple reactivity triggers
        const updates: Record<string, string> = {};

        updates.make = existingMotorVehicle.make;
        updates.model = existingMotorVehicle.model;

        // Apply updates if there are any
        if (Object.keys(updates).length > 0) {
          // Use nextTick or batch updates if possible, otherwise apply sequentially
          Object.entries(updates).forEach(([field, value]) => {
            setFieldValue(field as 'make' | 'model', value);
          });

          hasAutoPopulated = true;
        }
      }
    },
    { immediate: true }, // Check on initial mount
  );

  // Reset auto-population flag if both fields are manually cleared
  watch([make, model], ([newMake, newModel]) => {
    if (!newMake && !newModel) {
      hasAutoPopulated = false;
    }
  });
}
